import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatINR } from '../../../utils/currency';

const WarrantyTimelineChart = ({ warrantyData = [], fullView = false }) => {
  const [horizonMonths, setHorizonMonths] = useState(12);

  const timelineData = useMemo(() => {
    const monthlyData = {};
    const now = new Date();

    // Generate upcoming N months from current month
    for (let i = 0; i < horizonMonths; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthKey = d.toLocaleString('en-IN', { month: 'short', year: 'numeric' });
      monthlyData[monthKey] = {
        month: monthKey,
        expiring: 0,
        critical: 0,
        warning: 0,
        normal: 0,
        cost: 0
      };
    }

    // Process real warranty data
    warrantyData.forEach(asset => {
      if (!asset.warrantyEndDate) return;
      
      const date = new Date(asset.warrantyEndDate);
      if (isNaN(date.getTime())) return;

      const daysDiff = (date - now) / (1000 * 60 * 60 * 24);
      // Only include upcoming dates within the horizon (or within current month)
      if (daysDiff < -30 || daysDiff > (horizonMonths * 31)) return;

      const monthKey = date.toLocaleString('en-IN', { month: 'short', year: 'numeric' });
      
      if (monthlyData[monthKey]) {
        monthlyData[monthKey].expiring++;
        monthlyData[monthKey].cost += (asset.costRaw || 0);

        if (asset.daysRemaining <= 30) {
          monthlyData[monthKey].critical++;
        } else if (asset.daysRemaining <= 60) {
          monthlyData[monthKey].warning++;
        } else {
          monthlyData[monthKey].normal++;
        }
      }
    });

    return Object.values(monthlyData);
  }, [warrantyData, horizonMonths]);

  const getBarColor = (value, critical, warning) => {
    if (critical > 0) return '#DC2626';
    if (warning > 0) return '#D97706';
    return '#059669';
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg shadow-elevated p-3">
          <p className="font-semibold text-popover-foreground mb-1.5">{label}</p>
          <div className="space-y-1 text-xs">
            <div className="flex items-center justify-between space-x-4">
              <span className="text-error font-medium">Critical (≤30 days):</span>
              <span className="font-bold">{data?.critical || 0}</span>
            </div>
            <div className="flex items-center justify-between space-x-4">
              <span className="text-warning font-medium">Warning (31–60 days):</span>
              <span className="font-bold">{data?.warning || 0}</span>
            </div>
            <div className="flex items-center justify-between space-x-4">
              <span className="text-success font-medium">Normal (&gt;60 days):</span>
              <span className="font-bold">{data?.normal || 0}</span>
            </div>
            {data?.cost > 0 && (
              <div className="border-t border-border pt-1 mt-1 flex justify-between space-x-4 text-card-foreground">
                <span className="text-muted-foreground">Replacement Value:</span>
                <span className="font-bold text-success">{formatINR(data.cost, false)}</span>
              </div>
            )}
            <div className="border-t border-border pt-1 mt-1">
              <div className="flex items-center justify-between space-x-4 font-bold text-card-foreground">
                <span>Total Expiring:</span>
                <span className="text-primary">{data?.expiring || 0}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const totalExpiring = timelineData.reduce((sum, m) => sum + m.expiring, 0);
  const totalCost = timelineData.reduce((sum, m) => sum + m.cost, 0);
  const totalCritical = timelineData.reduce((sum, m) => sum + m.critical, 0);

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${fullView ? 'col-span-2' : ''}`}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={20} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Warranty Expiration Timeline</h3>
            <p className="text-xs text-muted-foreground">
              {horizonMonths}-Month dynamic forecast of expiring asset warranties
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Horizon Selector */}
          <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
            <button
              onClick={() => setHorizonMonths(6)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-smooth ${
                horizonMonths === 6 ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              6 Months
            </button>
            <button
              onClick={() => setHorizonMonths(12)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-smooth ${
                horizonMonths === 12 ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              12 Months
            </button>
            <button
              onClick={() => setHorizonMonths(24)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-smooth ${
                horizonMonths === 24 ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              24 Months
            </button>
          </div>

          <div className="hidden sm:flex items-center space-x-3 text-xs">
            <div className="flex items-center space-x-1">
              <div className="w-2.5 h-2.5 bg-error rounded-full"></div>
              <span className="text-muted-foreground">Critical (≤30d)</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2.5 h-2.5 bg-warning rounded-full"></div>
              <span className="text-muted-foreground">Warning (31–60d)</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2.5 h-2.5 bg-success rounded-full"></div>
              <span className="text-muted-foreground">Normal (&gt;60d)</span>
            </div>
          </div>
        </div>
      </div>

      <div className={`${fullView ? 'h-96' : 'h-80'}`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={timelineData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis 
              dataKey="month" 
              stroke="#64748B"
              fontSize={11}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="#64748B"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="expiring" radius={[4, 4, 0, 0]}>
              {timelineData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getBarColor(entry.expiring, entry.critical, entry.warning)} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <div className="flex items-center justify-between p-2.5 bg-muted/30 rounded border border-border">
            <span className="text-muted-foreground">Expiring in {horizonMonths}M:</span>
            <span className="font-bold text-foreground text-sm">{totalExpiring} assets</span>
          </div>
          <div className="flex items-center justify-between p-2.5 bg-muted/30 rounded border border-border">
            <span className="text-muted-foreground">Critical Urgency (≤30d):</span>
            <span className="font-bold text-error text-sm">{totalCritical} assets</span>
          </div>
          <div className="flex items-center justify-between p-2.5 bg-muted/30 rounded border border-border">
            <span className="text-muted-foreground">Est. Replacement Value:</span>
            <span className="font-bold text-success text-sm">{formatINR(totalCost, true)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarrantyTimelineChart;
